import { makeStyles } from '@material-ui/core';
import { Entity, EntityMetricsDefinition, SmartModuleType, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { v4 as uuidv4 } from 'uuid';
import { capitalizeAll, convertCollectionDataToMetricsDataPoints,
    DataPoint, GenericMessageBox, mergeMetricDataToMultiMetricDataPoints, MultiSeriesDataPoint } from '@moderepo/mode-app-components';
import { fetchTimeSeriesCollectionData, fetchTimeSeriesCollectionRawData,
    selectTimeSeriesCollectionData, selectTimeSeriesCollectionRawData, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import moment from 'moment';
import { EntityUIComponentCollection, EntityNumericMetricsCustomSettings, EntityUIComponentProps,
    MetricsDefinitionsAndMetricsExclusionCustomSettings,
    isMetricsDefinitionAndMetricsExclusionCustomSettings,
    isEntityNumericMetricsCustomSettings,
    useConvertTimeControlParamsToFetchDataTimeRangeParams,
    useSelectTranslatedEntityClasses,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings,
    filterMetricsByMetricsExclusionCustomSettings,
    getMetricsDefinitionMetricsId } from '..';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { SettingsErrorLogger } from '../../../utils/logger';
import { isObjectLiteral, isValidSettingStringValue, NoneValue, isValidSettingNumberValue, sortObjectAttributes } from '../../../utils/utils';
import { EntityPieChartContainer } from '../EntityPieChartContainer';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity } from '../../../utils/dataStateHooks';
import { MAX_RAW_DATA_COUNT } from '../../time-series/tsUtils';

const useStyle = makeStyles(() => {
    return {
        root: {
            '&.clickable': {
                cursor: 'pointer',
            },
        },
    };
}, { name: 'EntityPieChartViewUIComponent', index: 1 });

// How many RAW data point we want to show for each chart. This value will be used if the container does not provide a value.
const DEFAULT_RAW_DATA_COUNT = 200;

export interface PieChartViewSettings {
    readonly strokeWidth?: number;
    readonly innerRadius?: number;
    readonly borderColor?: string;
}

export const isPieChartViewSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is PieChartViewSettings => {
    const settings = obj as PieChartViewSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // chartViewSettings must be an object
    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: '"chartViewSettings" must be undefined or an Object.',
            value  : settings,
            path   : [...configPath, 'chartViewSettings'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.strokeWidth, false, false, 1, 10)) {
        return errorLogger?.logError({
            message: '"strokeWidth" must be undefined or an integer between 1 and 10 inclusive.',
            value  : settings.strokeWidth,
            path   : [...configPath, 'strokeWidth'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.innerRadius, false, false, 0, 100)) {
        return errorLogger?.logError({
            message: '"innerRadius" must be undefined or an integer between 0 and 100 inclusive.',
            value  : settings.innerRadius,
            path   : [...configPath, 'innerRadius'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.borderColor, false)) {
        return errorLogger?.logError({
            message: '"borderColor" must be undefined or a non-empty string.',
            value  : settings.borderColor,
            path   : [...configPath, 'borderColor'],
        }) ?? false;
    }

    return true;
};

/**
 * Interface for the EntityPieChartUIComponentCustomSettings's customSettings. Because 'EntityPieChartUIComponentCustomSettings' displays the
 * entity's metrics, we can have it extends the MetricsExclusionCustomSettings which comes with the options for excluding/including
 * metrics. We don't need to define those.
 */
export interface EntityPieChartViewUIComponentCustomSettings
    extends MetricsDefinitionsAndMetricsExclusionCustomSettings, EntityNumericMetricsCustomSettings {
    readonly icon?: string;
    readonly titleKey?: string;
    readonly dataCount?: number | undefined;

    // The collection aggregation type. Only needed for TSDB data and the default is 'avg'
    readonly aggregation?: TimeSeriesAggregation | undefined;

    // The collection aggregation type. Only needed for TSDB data and the default is 'avg'
    // used to know how to aggregate the TSDB data to display to the end user
    readonly displayValueAggregation?: TimeSeriesAggregation | undefined;

    readonly chartViewSettings?: PieChartViewSettings;
}

export interface EntityPieChartViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityPieChartViewUIComponentCustomSettings | undefined;
}

/**
 * Type guard function to validate the obj to make sure it is a type of EntityPieChartViewUIComponentCustomSettings
 */
export const isEntityPieChartViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityPieChartViewUIComponentCustomSettings => {
    const settings = obj as EntityPieChartViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // icon can be undefined or a string
    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.dataCount, false, false, 1, MAX_RAW_DATA_COUNT)) {
        return errorLogger?.logError({
            message: '"dataCount" must be undefined or an integer between 1 and 500 inclusive.',
            value  : settings.dataCount,
            path   : [...configPath, 'dataCount'],
        }) ?? false;
    }
    const supportedAggregations = [...Object.values(TimeSeriesAggregation), NoneValue];

    // aggregation and displayValueAggregation is optional but if it is provided, it must be one of the TimeSeriesAggregation
    if (!isValidSettingStringValue(settings.aggregation, false, false, supportedAggregations)) {
        return errorLogger?.logError({
            message: `"aggregation" must be undefined or one of the following values [${
                supportedAggregations.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.aggregation,
            path : [...configPath, 'aggregation'],
        }) ?? false;
    }
    // aggregation and displayValueAggregation is optional but if it is provided, it must be one of the TimeSeriesAggregation
    if (!isValidSettingStringValue(settings.displayValueAggregation, false, false, supportedAggregations)) {
        return errorLogger?.logError({
            message: `"displayValueAggregation" must be undefined or one of the following values [${
                supportedAggregations.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.displayValueAggregation,
            path : [...configPath, 'displayValueAggregation'],
        }) ?? false;
    }

    // chartViewSettings can be undefined or an Object. If it is an object, it must have the correct chart settings
    if (settings.chartViewSettings !== undefined) {
        if (!isPieChartViewSettings(
            settings.chartViewSettings, errorLogger, [...configPath, 'chartViewSettings'],
        )) {
            return false;
        }
    }
    // Because settings is also an MetricsExclusionCustomSettings, we can call this type guard function to validate the props defined
    // for "MetricsExclusionCustomSettings"
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because settings is also an EntityNumericMetricsCustomSettings, we can call this type guard function to validate the props defined
    // for "EntityNumericMetricsCustomSettings"
    if (!isEntityNumericMetricsCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};

interface FilteredData {
    readonly displayMetricName: string;
    readonly metricName?: string;
    readonly values: number[];
}

export interface AggregatedData {
    readonly displayMetricName: string;
    readonly value: number;
}

// Temporary Interface for storing params required for fetching TSDB aggregated data
interface FetchTSDBAggregatedDataParams {
    readonly homeId: number;
    readonly moduleId: string;
    readonly collectionId: string,
    readonly startTime: number;
    readonly endTime: number;
    readonly valueNames: string[];
    readonly aggregation?: TimeSeriesAggregation | undefined;
}


// Temporary Interface for storing params required for fetching TSDB raw data
interface FetchTSDBRawDataParams {
    readonly homeId: number;
    readonly moduleId: string;
    readonly collectionId: string,
    readonly timestamp: number;
    readonly valueNames: string[];
    readonly limit: number;
}

/**
 * Fetch all Entity metrics data belonged to the specified metricsDefinition
 */
const useFetchData = (
    entity: Entity | undefined,
    metricsToBeDisplayed: readonly EntityMetricsDefinition[],
    startTime: number,
    endTime: number,
    aggregation: TimeSeriesAggregation | undefined,
    dataCount: number,
): (MultiSeriesDataPoint<number> | undefined)[] | undefined => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingAggregatedData, setParamsForFetchingMissingAggregatedData] = useState<FetchTSDBAggregatedDataParams[]>();
    const [paramsForFetchingMissingRawData, setParamsForFetchingMissingRawData] = useState<FetchTSDBRawDataParams[]>();
    const hasErrors = useRef<boolean>(false);

    const result = useMemo(() => {
        if (!hasErrors.current && entity) {
            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingAggregatedData: FetchTSDBAggregatedDataParams [] = [];
            const newParamsForFetchingMissingRawData: FetchTSDBRawDataParams [] = [];

            const result = metricsToBeDisplayed.map((metricsDefinition) => {
                const smartModuleId = metricsDefinition.dataStoreConfiguration.moduleInstanceId;
                const collectionId = getMetricsDefinitionMetricsId(entity, metricsDefinition.metricsDefinitionId);

                if (aggregation) {
                // aggregation provided, make API call to fetch regular collection data with the given start/end time

                    const combinedData: {
                        numericMetrics: {
                            readonly [metricName: string]: DataPoint<number>[];
                        };
                    } = {
                        numericMetrics: {},
                    };

                    const valueNames = metricsDefinition?.numericMetrics?.map((metric) => {
                        return metric.name;
                    });

                    if (valueNames) {
                        // Get cached data from the data state
                        const cachedData = selectTimeSeriesCollectionData(
                            appDataState,
                            entity.homeId,
                            smartModuleId,
                            collectionId,
                            moment(startTime).toISOString(),
                            moment(endTime).toISOString(),
                            valueNames,
                            aggregation,
                        );

                        if (cachedData) {
                            const reformattedCollectionData = convertCollectionDataToMetricsDataPoints(cachedData, valueNames, undefined);
                            combinedData.numericMetrics = {
                                ...combinedData.numericMetrics,
                                ...reformattedCollectionData.numericMetrics,
                            };
                        } else {
                            // If we can't find data from cache, call setParamsForFetchingMissingAggregatedData
                            // to remember the params required for fetching the data.
                            // The useEffect below will take care of fetching the missing data.
                            newParamsForFetchingMissingAggregatedData.push({
                                homeId  : entity.homeId,
                                moduleId: smartModuleId,
                                collectionId,
                                startTime,
                                endTime,
                                valueNames,
                                aggregation,
                            });
                        }
                    }

                    // If we have all the data then return the result.
                    if (newParamsForFetchingMissingAggregatedData.length === 0) {
                        return {
                            numericMetrics: mergeMetricDataToMultiMetricDataPoints(combinedData.numericMetrics),
                        };
                    }

                    // Set the newParamsForFetchingMissingAggregatedData to remember that we need to fetch these missing data. But we only set it
                    // if the newParamsForFetchingMissingAggregatedData is different from the current paramsForFetchingMissingAggregatedData
                    setParamsForFetchingMissingAggregatedData((currentParams) => {
                        if (
                            !currentParams
                        || JSON.stringify(sortObjectAttributes(currentParams))
                        !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingAggregatedData))
                        ) {
                            return newParamsForFetchingMissingAggregatedData;
                        }
                        return currentParams;
                    });

                } else {
                    // no aggregation is provided, so we fetch RAW data

                    const valueNames = metricsDefinition?.numericMetrics?.map((metric) => {
                        return metric.name;
                    });
                    if (valueNames) {
                        // Get cached data from the data state
                        const cachedData = selectTimeSeriesCollectionRawData(
                            appDataState,
                            entity.homeId,
                            smartModuleId,
                            collectionId,
                            moment(startTime).toISOString(),
                            dataCount,
                            valueNames,
                            [],
                        );

                        if (cachedData) {
                            // We have all the data, we can return the result now.
                            const reformattedCollectionData = convertCollectionDataToMetricsDataPoints(cachedData, valueNames);
                            return {
                                numericMetrics: mergeMetricDataToMultiMetricDataPoints(reformattedCollectionData.numericMetrics),
                            };
                        }

                        // If we can't find data from cache, call setParamsForFetchingMissingAggregatedData
                        // to remember the params required for fetching the
                        // data. The useEffect below will take care of fetching the missing data.
                        newParamsForFetchingMissingRawData.push({
                            homeId   : entity.homeId,
                            moduleId : smartModuleId,
                            collectionId,
                            limit    : dataCount,
                            timestamp: startTime,
                            valueNames,
                        });
                    }

                    // Set the newParamsForFetchingMissingRawData to remember that we need to fetch these missing data. But we only set it if the
                    // newParamsForFetchingMissingRawData is different from the current paramsForFetchingMissingRawData
                    setParamsForFetchingMissingRawData((currentParams) => {
                        if (
                            !currentParams
                    || JSON.stringify(sortObjectAttributes(currentParams))
                        !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingRawData))
                        ) {
                            return newParamsForFetchingMissingRawData;
                        }
                        return currentParams;
                    });
                }
                return undefined;
            });
            return result;
        }
        return undefined;
    }, [entity, metricsToBeDisplayed, aggregation, appDataState, startTime, endTime, dataCount]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingAggregatedData && paramsForFetchingMissingAggregatedData.length > 0) {
            paramsForFetchingMissingAggregatedData.forEach((params) => {
                appDataDispatch(fetchTimeSeriesCollectionData(
                    params.homeId,
                    params.moduleId,
                    params.collectionId,
                    moment(params.startTime).toISOString(),
                    moment(params.endTime).toISOString(),
                    params.valueNames,
                    params.aggregation,
                )).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
        if (paramsForFetchingMissingRawData && paramsForFetchingMissingRawData.length > 0) {
            paramsForFetchingMissingRawData.forEach((params) => {
                appDataDispatch(fetchTimeSeriesCollectionRawData(
                    params.homeId,
                    params.moduleId,
                    params.collectionId,
                    moment(params.timestamp).toISOString(),
                    params.limit,
                    params.valueNames,
                    [],
                )).catch(() => {
                    hasErrors.current = true;
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingAggregatedData, paramsForFetchingMissingRawData]);

    if (result) {
        const mergedObject = result.map((ele) => {
            if (ele?.numericMetrics) {
                return ele.numericMetrics;
            }
            return undefined;
        });
        return mergedObject.flat(1);
    }
    return undefined;
};

const aggregateData = (
    aggregationType: TimeSeriesAggregation | undefined,
    aggregationData: readonly FilteredData[],
): AggregatedData[] => {

    const result: AggregatedData[] = [];
    switch (aggregationType) {
        case TimeSeriesAggregation.MIN:
            aggregationData.forEach((filteredData, idx) => {
                const filteredMetricName = filteredData.displayMetricName;
                if (filteredData.values.length === 0) {
                    result[idx] = { displayMetricName: filteredMetricName, value: 0 };
                } else {
                    result[idx] = { displayMetricName: filteredMetricName, value: Math.min(...filteredData.values) };
                }
            });
            break;
        case TimeSeriesAggregation.MAX:
            aggregationData.forEach((filteredData, idx) => {
                const filteredMetricName = filteredData.displayMetricName;
                if (filteredData.values.length === 0) {
                    result[idx] = { displayMetricName: filteredMetricName, value: 0 };
                } else {
                    result[idx] = { displayMetricName: filteredMetricName, value: Math.max(...filteredData.values) };
                }
            });
            break;
        case TimeSeriesAggregation.COUNT:
            aggregationData.forEach((filteredData, idx) => {
                const filteredMetricName = filteredData.displayMetricName;
                result[idx] = { displayMetricName: filteredMetricName, value: filteredData.values.length };
            });
            break;
        case TimeSeriesAggregation.SUM:
            aggregationData.forEach((filteredData, idx) => {
                const filteredMetricName = filteredData.displayMetricName;
                if (filteredData.values.length === 0) {
                    result[idx] = { displayMetricName: filteredMetricName, value: 0 };
                } else {
                    const filteredDataSum = filteredData.values.reduce((accum, value) => { return accum + value; });
                    result[idx] = { displayMetricName: filteredMetricName, value: filteredDataSum };
                }
            });
            break;
        default:
            aggregationData.forEach((filteredData, idx) => {
                const filteredMetricName = filteredData.displayMetricName;
                if (filteredData.values.length === 0) {
                    result[idx] = { displayMetricName: filteredMetricName, value: 0 };
                } else {
                    const filteredDataSum = filteredData.values.reduce((accum, value) => { return accum + value; });
                    const filteredDataAverage = filteredDataSum / filteredData.values.length;
                    result[idx] = { displayMetricName: filteredMetricName, value: filteredDataAverage };
                }
            });
    }

    return result;
};

const combineNumericMetrics = ((
    aggregationType: TimeSeriesAggregation | undefined,
    metricsDefinitions: readonly EntityMetricsDefinition[],
    allMetricsData: (MultiSeriesDataPoint<number> | undefined)[]) => {

    const result: FilteredData[] = [];
    const allMetricData: number[] = [];

    metricsDefinitions.forEach((entityMetric) => {
        if (entityMetric?.numericMetrics) {
            entityMetric?.numericMetrics.forEach((metric) => {
                result.push({ displayMetricName: metric.displayName ?? metric.name, metricName: metric.name, values: [] });
            });
        }
    });

    allMetricsData.forEach((metricDataElement) => {
        if (typeof metricDataElement?.values === 'object') {
            Object.entries(metricDataElement.values).forEach((metricValue) => {
                const metricData = metricValue[1];
                const metricName = metricValue[0];

                const metricDataObj = result.find((resultMetric) => { return resultMetric.metricName === metricName; });
                if (typeof metricData === 'number') {
                    metricDataObj?.values.push(metricData);
                    allMetricData.push(metricData);
                }
            });
        }
    });

    // if every value of the metricData is 0 or undefined, there is no data to show
    if (allMetricData.every((value) => { return value === 0 || value === undefined; })) {
        return undefined;
    }
    return aggregateData(aggregationType, result);
});

export const EntityPieChartViewUIComponent: React.FC<EntityPieChartViewUIComponentProps> = (
    props: EntityPieChartViewUIComponentProps,
) => {
    const classes = useStyle();

    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataOptions = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);

    // Give the component a unique ID. We need to prepend this ID to the chart element's ID to make sure the chart element ID is unique.
    const componentIdRef = useRef<string>(uuidv4());


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);

    /**
      * An entity can have more then 1 metrics definitions. Not all metrics definitions need to be displayed because some
      * might need to be excluded based on the uiSettings. We we first need to filter out metricsDefinitions that we don't
      * need to display
      */
    const filteredMetricsDefinitions = useMemo(() => {
        // First, filter out all metricsDefinitions that are NOT TSDB. This component can only display charts for TSDB metrics definitions
        const tsdbMetricsDef = selectedEntityClass?.metricsDefinitions?.filter((metricsDef) => {
            return metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB
            && metricsDef.numericMetrics && metricsDef.numericMetrics.length > 0;
        });

        // If we only need to show AGGREGATED metric type then we will filter out ALL metrics definition which don't have numeric metrics.
        // Else, we show all metrics definition of type TSDB
        const filteredMetricsDef = (props.customSettings?.displayValueAggregation)
            ? tsdbMetricsDef?.filter((metricsDef) => {
                return metricsDef.numericMetrics && metricsDef.numericMetrics.length > 0;
            })
            : tsdbMetricsDef;

        return filterMetricsDefinitionsByMetricsExclusionCustomSettings(filteredMetricsDef ?? [], props.customSettings);
    }, [props.customSettings, selectedEntityClass?.metricsDefinitions]);

    const metricsToBeDisplayed = useMemo(() => {

        const result: EntityMetricsDefinition[] = [];

        if (filteredMetricsDefinitions && selectedEntity) {
            filteredMetricsDefinitions.map((metricDef) => {
                // If "metricsExclusionSettings" is provided, filter out metrics based on "metricsExclusionSettings"
                const filteredNumericMetrics = [...filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId,
                    metricDef.numericMetrics,
                    props.customSettings,
                    true,
                )];
                // Only add metrics from metricDef if there is at least 1 visible valueNames or tagNames
                if (filteredNumericMetrics.length > 0) {
                    result.push({
                        metricsDefinitionId   : metricDef.metricsDefinitionId,
                        displayName           : metricDef.displayName,
                        metricsType           : metricDef.metricsType,
                        dataStoreConfiguration: metricDef.dataStoreConfiguration,
                        numericMetrics        : filteredNumericMetrics,
                    });
                }
                return undefined;
            });
        }
        return result;
    }, [filteredMetricsDefinitions, props.customSettings, selectedEntity]);

    /**
     * Fetch data for all metrics
    */
    const allMetricsData = useFetchData(
        selectedEntity,
        metricsToBeDisplayed,
        fetchDataOptions.startTime,
        fetchDataOptions.endTime,
        props.customSettings?.aggregation,
        props.customSettings?.dataCount ?? DEFAULT_RAW_DATA_COUNT,
    );

    const chartData = useMemo(() => {
        if (!metricsToBeDisplayed || !allMetricsData || allMetricsData.length === 0) {
            return undefined;
        }

        const numericChartData = combineNumericMetrics(props.customSettings?.displayValueAggregation, metricsToBeDisplayed, allMetricsData);
        return numericChartData;

    }, [allMetricsData, metricsToBeDisplayed, props.customSettings?.displayValueAggregation]);

    return (
        <div className={classes.root}>
            {chartData && chartData.length > 0 && (
                <EntityPieChartContainer
                    title={props.customSettings?.titleKey ? trans(props.customSettings?.titleKey) : undefined}
                    key={`${props.customSettings?.titleKey}-${componentIdRef.current}}`}
                    icon={props.customSettings?.icon ?? 'pie_chart'}
                    chartIdPrefix={componentIdRef.current}
                    strokeWidth={props.customSettings?.chartViewSettings?.strokeWidth}
                    innerRadius={props.customSettings?.chartViewSettings?.innerRadius}
                    borderColor={props.customSettings?.chartViewSettings?.borderColor}
                    chartData={chartData}
                />
            )}
            {/*
                If there are no metric or no data then show empty data message
            */}
            {(!chartData || chartData.length === 0) && (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                    })}
                />
            )}

        </div>
    );
};

// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityPieChartViewUIComponent', EntityPieChartViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Pie_Chart_Comp',
    {
        componentFunc          : EntityPieChartViewUIComponent,
        customSettingsValidator: isEntityPieChartViewUIComponentCustomSettings,
    },
);
